import { useEffect, useState, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { formatDuration } from "../../../../../../config/global";
import { COMMON_ASPECT_RATIOS } from '../../../../../../config/layoutConstants';
import LazyImage from '../../../../../SharedComponents/ShowMedia/LazyImage';
import VisualAreaContent from '../../Custom/Controls/VisualAreaContent';
import { usePreviewCampaignContent, useGetCampaignLayerContent, useGetPlaylistLayerContent } from '../../../../../../queries';

// Sub-component for individual canvas area
const PreviewAreaCanvasItem = ({ area, index, areasCount, forcedItem, onActiveItemChange }) => {
    const { t } = useTranslation();
    // We fetch draft content just to check if it exists for the placeholder
    const { data } = useGetCampaignLayerContent(area.id, "MAIN");
    const hasContent = data && data.length > 0;

    return (
        <div
            className="absolute overflow-hidden bg-black border border-slate-800"
            style={{
                left: `${area.start_margin || 0}%`,
                top: `${area.top_margin || 0}%`,
                width: `${area.width || 0}%`,
                height: `${area.height || 0}%`,
                zIndex: areasCount - index,
            }}
        >
            {hasContent ? (
                <div className="w-full h-full relative">
                    <VisualAreaContent 
                        areaId={area.id} 
                        settings={area.settings?.live} 
                        showTimeline={false} 
                        forcedItem={forcedItem}
                        onItemChange={onActiveItemChange}
                        cleanWrapper={true}
                    />
                    
                    <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-md text-slate-900 px-4 py-2 rounded-xl text-lg font-bold border border-slate-200 shadow-lg z-20 flex items-center gap-2">
                        <i className="bx bx-layer text-indigo-600"></i>
                        <span>{t('layouts.setup_area')} {index + 1}</span>
                    </div>
                </div>
            ) : (
                <div className="w-full h-full bg-slate-900 flex items-center justify-center border border-slate-800 m-0">
                    <div className="text-center">
                        <i className="bx bx-video-off text-3xl text-slate-800 mb-2"></i>
                        <p className="text-xs text-slate-700 font-bold uppercase tracking-widest">{t('layouts.setup_no_content')}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

// Sub-component for sidebar area item
const PreviewSidebarAreaItem = ({ area, index, activeContent, areaStartTimes, onSelect }) => {
    const { t } = useTranslation();
    const { data: rawData } = useGetCampaignLayerContent(area.id, "MAIN");
    
    const playlistId = useMemo(() => {
        if (!rawData || rawData.length === 0) return null;
        const playlistItem = rawData.find(item => item.type === "EVENT" && item.playlist_id);
        return playlistItem?.playlist_id || null;
    }, [rawData]);

    const { data: playlistMediaData } = useGetPlaylistLayerContent(playlistId, "MAIN");

    const contents = useMemo(() => {
        if (!rawData) return [];
        
        let initialList = [];
        if (Array.isArray(rawData)) {
            initialList = rawData;
        } else if (rawData.mainContent) {
            initialList = rawData.mainContent;
        } else if (rawData.MAIN) {
            initialList = rawData.MAIN;
        }

        const expanded = [];
        initialList.forEach(item => {
            if (item.type === "EVENT" && item.playlist_id) {
                if (playlistMediaData) {
                    let pList = playlistMediaData?.mainContent || playlistMediaData?.MAIN || (Array.isArray(playlistMediaData) ? playlistMediaData : []);
                    if (Array.isArray(pList) && pList.length > 0 && Array.isArray(pList[0])) pList = pList.flat();
                    
                    pList.forEach((mediaItem) => {
                        expanded.push({
                            ...mediaItem,
                            is_muted: item.is_muted,
                            maintain_aspect_ratio: item.maintain_aspect_ratio,
                        });
                    });
                }
            } else {
                expanded.push(item);
            }
        });
        
        return expanded.map((item, idx) => ({
             ...item,
             _instanceId: `${item.id || item.media_id}_${area.id}_${idx}`
        }));
    }, [rawData, playlistMediaData, area.id]);

    const getProgressStyle = (startTime, duration) => {
        if (!startTime || !duration) return { background: "transparent" };
        const now = new Date().getTime();
        const start = startTime.getTime();
        const end = start + duration * 1000;
        if (now >= end) return { backgroundColor: "#8c86f15e" };
        const percent = Math.max(1, Math.min(100, ((now - start) / (end - start)) * 100));
        return {
            background: `linear-gradient(to right, #8c86f15e ${percent}%, transparent ${percent}%)`,
            transition: 'background 0.5s ease-in-out'
        };
    };

    return (
        <div className="overflow-hidden rounded-xl bg-white border border-gray-200 shadow-sm">
            <div className="px-3 py-2 bg-gray-50/80 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded flex items-center justify-center bg-indigo-50 text-indigo-600 text-xs font-bold border border-indigo-100">
                        {index + 1}
                    </span>
                    <span className="text-xs font-semibold text-slate-700 truncate max-w-[150px]" title={area.name}>{area.name}</span>
                </div>
                <span className="text-[10px] font-bold text-slate-500 bg-white border border-gray-200 px-1.5 py-0.5 rounded-md shadow-sm">
                    {t('layouts.setup_items_count', {count: contents.length})}
                </span>
            </div>
            
            <div className="divide-y divide-gray-100">
                {contents.length > 0 ? (
                    contents.map((item, i) => {
                        const isActive = activeContent?._instanceId === item._instanceId;
                        const durationVal = parseFloat(item.total_duration || item.custom_duration) || 10;
                        const itemStyle = isActive ? getProgressStyle(areaStartTimes[area.id], durationVal) : {};
                        
                        return (
                            <div 
                                key={item._instanceId}
                                onClick={() => onSelect(area.id, item)}
                                className={`px-3 py-2 flex items-center gap-3 cursor-pointer transition-colors ${isActive ? 'bg-indigo-50' : 'hover:bg-gray-50'}`}
                                style={itemStyle}
                            >
                                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden border border-gray-200 flex-shrink-0 relative shadow-sm">
                                    <LazyImage media={item} styleClass="w-full h-full object-cover" />
                                    {isActive && (
                                        <div className="absolute inset-0 bg-indigo-500/20 flex items-center justify-center backdrop-blur-[1px]">
                                            <div className="w-6 h-6 rounded-full bg-white/90 flex items-center justify-center shadow-md">
                                                 <i className="bx bx-play text-indigo-600 text-sm ml-0.5"></i>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                
                                <div className="flex-1 min-w-0">
                                    <p className={`text-xs font-semibold truncate ${isActive ? 'text-indigo-700' : 'text-slate-700'}`}>
                                        {item.media_name || (item.playlist_name && "Playlist: " + item.playlist_name) || (item.layout_name && "Layout: " + item.layout_name) || item.file_name?.split(".")[0] || item.name || t('layouts.setup_untitled_content')}
                                    </p>
                                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                                        <span className="text-[10px] text-slate-500 flex items-center gap-1 font-medium bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200">
                                            <i className="bx bx-time text-slate-400"></i> {formatDuration(durationVal)}
                                        </span>
                                        <span className="text-[10px] text-slate-500 uppercase px-1.5 py-0.5 bg-gray-100 rounded font-bold tracking-wider border border-gray-200">{item.media_type}</span>
                                        {(item.is_muted == 1 || item.is_muted === true) && (
                                            <div title={t('layouts.setup_muted')} className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 border border-amber-100/60 shadow-sm">
                                                <i className="bx bx-volume-mute text-[10px] text-amber-500"></i>
                                                <span className="text-[9px] font-bold text-amber-600 uppercase tracking-wide">{t('layouts.setup_muted')}</span>
                                            </div>
                                        )}
                                        {(item.maintain_aspect_ratio == 0 || item.maintain_aspect_ratio === false) && (
                                             <div title={t('layouts.setup_fill_screen')} className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-sky-50 border border-sky-100/60 shadow-sm">
                                                <i className="bx bx-fullscreen text-[10px] text-sky-500"></i>
                                                <span className="text-[9px] font-bold text-sky-600 uppercase tracking-wide">{t('layouts.setup_fill')}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {isActive && <div className="w-2 h-2 rounded-full bg-indigo-500 shadow-[0_0_8px_rgba(99,102,241,0.6)] animate-pulse"></div>}
                            </div>
                        );
                    })
                ) : (
                    <div className="px-3 py-6 text-center bg-gray-50/50">
                        <p className="text-xs text-slate-400 italic">{t('layouts.setup_no_content_assigned')}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

function PreviewLayoutModal({ onClose, layoutName, screens_setting }) {
    const { t } = useTranslation();
    const { selectedLayout, layoutAspectRatio } = useSelector((state) => state.layout);
    const [tick, setTick] = useState(0);
    const [areaContents, setAreaContents] = useState({});
    const [areaStartTimes, setAreaStartTimes] = useState({});

    const containerRef = useRef(null);
    const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });
    const [userZoom, setUserZoom] = useState(1); // 1 = Fit to screen

    useEffect(() => {
        const updateDimensions = () => {
            if (containerRef.current) {
                const { clientWidth, clientHeight } = containerRef.current;
                setContainerDimensions({ width: clientWidth, height: clientHeight });
            }
        };

        updateDimensions();
        window.addEventListener('resize', updateDimensions);
        
        // Small delay to ensure modal is rendered
        const timer = setTimeout(updateDimensions, 100);
        const interval = setInterval(() => setTick((t) => t + 1), 1000);
        
        return () => {
            window.removeEventListener('resize', updateDimensions);
            clearInterval(interval);
            clearTimeout(timer);
        };
    }, []);

    const [previewAspectRatio, setPreviewAspectRatio] = useState(null);
    const [isRatioDropdownOpen, setIsRatioDropdownOpen] = useState(false);
    
    // Close dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isRatioDropdownOpen && !event.target.closest('.ratio-dropdown-container')) {
                setIsRatioDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isRatioDropdownOpen]);

    const activeRatio = previewAspectRatio || layoutAspectRatio || selectedLayout?.aspect_ratio || { width: 1920, height: 1080 };
    
    // Resolve dimensions to avoid tiny boxes (e.g. 16x9 px)
    const displayDimensions = useMemo(() => {
        const w = parseFloat(activeRatio.width);
        const h = parseFloat(activeRatio.height);
        
        // Check common ratios first
        const ratioKey = `${w}:${h}`;
        if (COMMON_ASPECT_RATIOS[ratioKey]) {
            return COMMON_ASPECT_RATIOS[ratioKey];
        }
        
        if (COMMON_ASPECT_RATIOS[`${parseInt(w)}:${parseInt(h)}`]) {
            return COMMON_ASPECT_RATIOS[`${parseInt(w)}:${parseInt(h)}`];
        }

        // If dimensions are small (likely just ratio numbers), scale them up
        if (w < 200 || h < 200) {
           const scale = 1920 / w; // Normalize to 1920 width
           return { width: w * scale, height: h * scale };
        }
        
        return { width: w, height: h };
    }, [activeRatio]);

    const baseScale = useMemo(() => {
        if (!containerDimensions.width || !displayDimensions.width) return 0.5;
        // Padding 32px total
        const availableW = containerDimensions.width - 32; 
        const availableH = containerDimensions.height - 32; 
        
        return Math.min(
            availableW / displayDimensions.width, 
            availableH / displayDimensions.height
        );
    }, [containerDimensions, displayDimensions]);

    const finalScale = baseScale * userZoom;
    const scaledWidth = displayDimensions.width * finalScale;
    const scaledHeight = displayDimensions.height * finalScale;

    const aspectRatio = displayDimensions;
    const rawAreas = screens_setting || selectedLayout?.screens_setting || [];
    
    const areas = useMemo(() => {
        const seen = new Set();
        return rawAreas.filter(a => {
            if (seen.has(a.id)) return false;
            seen.add(a.id);
            return true;
        }).sort((a, b) => {
            const getOrder = (item) => {
                const liveOrder = item.settings?.live?.order;
                if (liveOrder !== undefined && liveOrder !== null) return Number(liveOrder);
                if (item.order !== undefined && item.order !== null) return Number(item.order);
                return parseInt(item.id, 10) || 0;
            };
            return getOrder(a) - getOrder(b);
        });
    }, [rawAreas]);

    return (
        <div 
            className="fixed inset-0 z-[9999] flex items-center justify-center p-6 animate-in fade-in duration-300"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>

            <div className="relative bg-white/95 backdrop-blur-2xl rounded-3xl shadow-2xl max-w-[95vw] w-full max-h-[95vh] min-h-[80vh] overflow-hidden flex flex-col animate-in zoom-in duration-300 border border-gray-200 ring-1 ring-black/5">
                {/* Header */}
                <div className="relative z-50 px-6 py-3 border-b border-gray-200 bg-white/50 backdrop-blur-lg flex-shrink-0">
                    <div className="relative flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center border border-indigo-100 shadow-sm">
                                <i className="bx bx-desktop text-lg text-indigo-500"></i>
                            </div>
                            <div>
                                <h3 className="text-base font-bold text-slate-800 tracking-tight leading-tight">{layoutName || t('layouts.setup_layout_preview')}</h3>
                                <div className="flex items-center gap-1.5">
                                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 shadow-[0_0_4px_rgba(34,197,94,0.4)]"></span>
                                    <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider leading-none">{t('layouts.setup_live_preview')}</p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                             {/* Aspect Ratio Simulator */}
                            <div className="relative ratio-dropdown-container">
                                <button
                                    onClick={() => setIsRatioDropdownOpen(!isRatioDropdownOpen)}
                                    className={`flex items-center gap-2.5 px-3 py-1.5 rounded-xl transition-all duration-200 border group min-w-[150px] justify-between relative overflow-hidden ${
                                        isRatioDropdownOpen || previewAspectRatio 
                                        ? 'bg-indigo-50/80 border-indigo-200 shadow-indigo-100/50 shadow-md text-indigo-700' 
                                        : 'bg-white border-gray-200/80 shadow-sm hover:shadow-md text-slate-700 hover:border-gray-300'
                                    }`}
                                    title="Simulate aspect ratio"
                                >
                                    <div className="flex items-center gap-2.5 z-10">
                                        <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${
                                            previewAspectRatio ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-500 group-hover:bg-slate-200'
                                        }`}>
                                            <i className={`text-xs ${
                                                !previewAspectRatio ? 'bx bx-expand' : 
                                                previewAspectRatio.width > previewAspectRatio.height ? 'bx bx-tv' : 
                                                previewAspectRatio.width < previewAspectRatio.height ? 'bx bx-mobile' : 'bx bx-grid-alt'
                                            }`}></i>
                                        </div>
                                        <div className="flex flex-col items-start leading-none">
                                            <span className="text-[9px] font-bold opacity-60 uppercase tracking-wider mb-0.5">{t('layouts.setup_simulation')}</span>
                                            <span className="text-xs font-bold truncate max-w-[90px]">
                                                {previewAspectRatio ? `${previewAspectRatio.width}:${previewAspectRatio.height}` : t('layouts.setup_default')}
                                            </span>
                                        </div>
                                    </div>
                                    <i className={`bx bx-chevron-down opacity-50 transition-transform duration-300 z-10 ${isRatioDropdownOpen ? 'rotate-180' : ''}`}></i>
                                    
                                    {/* Subtle Animated Background for Active State */}
                                    {previewAspectRatio && <div className="absolute inset-0 bg-gradient-to-r from-indigo-50 to-white opacity-50 z-0"></div>}
                                </button>

                                {/* Creative Dropdown Menu */}
                                {isRatioDropdownOpen && (
                                    <div className="absolute top-full right-0 mt-2 w-[340px] bg-white/95 backdrop-blur-xl rounded-2xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)] border border-white ring-1 ring-black/5 p-3 animate-in fade-in zoom-in-95 duration-200 origin-top-right overflow-hidden flex flex-col gap-3 z-[100]">
                                        
                                        {/* Header */}
                                        <div className="flex items-center justify-between px-2 pt-1">
                                            <div>
                                                <h4 className="text-xs font-bold text-slate-800">{t('layouts.setup_preview_simulator')}</h4>
                                                <p className="text-[10px] text-slate-500 font-medium">{t('layouts.setup_preview_simulator_subtitle')}</p>
                                            </div>
                                            {!previewAspectRatio && (
                                                <span className="px-2 py-0.5 rounded-full bg-green-50 text-green-600 border border-green-100 text-[10px] font-bold">{t('layouts.setup_active_default')}</span>
                                            )}
                                        </div>

                                        {/* Grid Options */}
                                        <div className="grid grid-cols-2 gap-2">
                                            {/* Reset / Standard Option */}
                                            <button
                                                onClick={() => {
                                                    setPreviewAspectRatio(null);
                                                    setIsRatioDropdownOpen(false);
                                                }}
                                                className={`col-span-2 relative p-2.5 rounded-xl border transition-all duration-200 flex items-center gap-3 group overflow-hidden ${
                                                    !previewAspectRatio 
                                                    ? 'bg-gradient-to-br from-indigo-500 to-violet-600 border-transparent text-white shadow-lg shadow-indigo-200' 
                                                    : 'bg-gray-50 border-gray-100 hover:bg-white hover:border-gray-200 hover:shadow-md'
                                                }`}
                                            >
                                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shadow-sm ${!previewAspectRatio ? 'bg-white/20 backdrop-blur-sm text-white' : 'bg-white text-slate-400'}`}>
                                                    <i className="bx bx-been-here text-xl"></i>
                                                </div>
                                                <div className="text-left flex-1 z-10">
                                                    <p className={`text-xs font-bold ${!previewAspectRatio ? 'text-white' : 'text-slate-700'}`}>{t('layouts.setup_original_layout')}</p>
                                                    <p className={`text-[10px] ${!previewAspectRatio ? 'text-indigo-100' : 'text-slate-500'}`}>{t('layouts.setup_use_saved_ratio')}</p>
                                                </div>
                                                {!previewAspectRatio && <i className="bx bx-check-circle text-white/50 text-4xl absolute -right-2 -bottom-2 rotate-[-15deg]"></i>}
                                            </button>

                                            {/* Ratio List */}
                                            {Object.entries(COMMON_ASPECT_RATIOS).map(([key, dims]) => {
                                                const isActive = previewAspectRatio && parseFloat(previewAspectRatio.width) === dims.width && parseFloat(previewAspectRatio.height) === dims.height;
                                                const ratio = dims.width / dims.height;
                                                
                                                let iconClass = "bx bx-square";
                                                if (key === "16:9") iconClass = "bx bx-tv";
                                                if (key === "9:16") iconClass = "bx bx-mobile";
                                                if (key === "21:9") iconClass = "bx bx-film";
                                                if (key === "4:3") iconClass = "bx bx-laptop";
                                                if (key === "1:1") iconClass = "bx bxl-instagram";
                                                
                                                return (
                                                    <button
                                                        key={key}
                                                        onClick={() => {
                                                            setPreviewAspectRatio({ width: dims.width, height: dims.height });
                                                            setIsRatioDropdownOpen(false);
                                                        }}
                                                        className={`relative p-2 rounded-xl border text-left transition-all duration-200 group flex items-start gap-2.5 ${
                                                            isActive 
                                                            ? 'bg-indigo-50 border-indigo-200 ring-1 ring-indigo-500/20 z-10' 
                                                            : 'bg-white border-gray-100 hover:border-gray-300 hover:shadow-md hover:z-10'
                                                        }`}
                                                    >
                                                        <div className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${isActive ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-50 text-slate-400 group-hover:bg-slate-100 group-hover:text-slate-600'}`}>
                                                            <i className={`${iconClass} text-lg`}></i>
                                                        </div>
                                                        
                                                        <div className="min-w-0 flex-1">
                                                            <div className="flex items-end justify-between">
                                                                <span className={`text-xs font-bold ${isActive ? 'text-indigo-700' : 'text-slate-700'}`}>{key}</span>
                                                            </div>
                                                            <div className="text-[9px] font-mono opacity-60 mb-0.5">{dims.width}x{dims.height}</div>
                                                            
                                                            <div className="w-full h-1 bg-gray-100 rounded-full overflow-hidden mt-1">
                                                                <div 
                                                                    className={`h-full rounded-full ${isActive ? 'bg-indigo-500' : 'bg-slate-300 group-hover:bg-slate-400'}`}
                                                                    style={{ width: `${Math.min(100, (ratio / 2.3) * 100)}%` }} 
                                                                ></div>
                                                            </div>
                                                        </div>
                                                        
                                                        {isActive && <div className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-indigo-500"></div>}
                                                    </button>
                                                );
                                            })}

                                            {/* Resolution Options */}
                                            <div className="col-span-2 px-1 pt-2 pb-1">
                                                <h5 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-gray-100 pb-1">{t('layouts.setup_standard_resolutions')}</h5>
                                            </div>

                                            {[
                                                { label: "4K UHD", sub: "Ultra High Def", w: 3840, h: 2160, icon: "bx bx-aperture" },
                                                { label: "2K QHD", sub: "Quad HD", w: 2560, h: 1440, icon: "bx bx-image" },
                                                { label: "FHD", sub: "1080p", w: 1920, h: 1080, icon: "bx bx-tv" },
                                                { label: "HD", sub: "720p", w: 1280, h: 720, icon: "bx bx-play-circle" },
                                                { label: "Vertical 4K", sub: "Portrait Signage", w: 2160, h: 3840, icon: "bx bx-mobile" },
                                                { label: "Vertical FHD", sub: "Portrait 1080p", w: 1080, h: 1920, icon: "bx bx-mobile-alt" }
                                            ].map((res) => {
                                                const isActive = previewAspectRatio && parseFloat(previewAspectRatio.width) === res.w && parseFloat(previewAspectRatio.height) === res.h;
                                                
                                                return (
                                                    <button
                                                        key={res.label}
                                                        onClick={() => {
                                                            setPreviewAspectRatio({ width: res.w, height: res.h });
                                                            setIsRatioDropdownOpen(false);
                                                        }}
                                                        className={`relative p-2 rounded-xl border text-left transition-all duration-200 group flex items-start gap-2.5 ${
                                                            isActive 
                                                            ? 'bg-indigo-50 border-indigo-200 ring-1 ring-indigo-500/20 z-10' 
                                                            : 'bg-white border-gray-100 hover:border-gray-300 hover:shadow-md hover:z-10'
                                                        }`}
                                                    >
                                                        <div className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${isActive ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-50 text-slate-400 group-hover:bg-slate-100 group-hover:text-slate-600'}`}>
                                                            <i className={`${res.icon} text-lg`}></i>
                                                        </div>
                                                        
                                                        <div className="min-w-0 flex-1">
                                                            <div className="flex items-end justify-between">
                                                                <span className={`text-xs font-bold ${isActive ? 'text-indigo-700' : 'text-slate-700'}`}>{res.label}</span>
                                                            </div>
                                                            <div className="text-[9px] font-mono opacity-60 mb-0.5">{res.w}x{res.h}</div>
                                                            <div className="text-[9px] text-slate-400 leading-none">{res.sub}</div>
                                                        </div>
                                                        
                                                        {isActive && <div className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-indigo-500"></div>}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>

                        {/* Divider */}
                        <div className="w-px h-6 bg-gray-200 hidden sm:block"></div>

                        {/* Zoom Controls */}
                        <div className="flex items-center bg-gray-100/80 rounded-lg p-0.5 border border-gray-200/60 shadow-inner">
                            <button 
                                onClick={() => setUserZoom(prev => Math.max(0.2, prev - 0.1))}
                                className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-white hover:shadow-sm text-slate-600 transition-all"
                                title={t('layouts.setup_zoom_out')}
                            >
                                <i className="bx bx-minus"></i>
                            </button>
                            <span className="text-[11px] font-bold text-slate-600 w-10 text-center select-none">
                                {Math.round(userZoom * 100)}%
                            </span>
                            <button 
                                onClick={() => setUserZoom(prev => Math.min(3, prev + 0.1))}
                                className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-white hover:shadow-sm text-slate-600 transition-all"
                                title={t('layouts.setup_zoom_in')}
                            >
                                <i className="bx bx-plus"></i>
                            </button>
                            <div className="w-px h-4 bg-gray-300 mx-1"></div>
                            <button 
                                onClick={() => setUserZoom(1)}
                                className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-white hover:shadow-sm text-slate-600 transition-all"
                                title={t('layouts.setup_fit_to_screen')}
                            >
                                <i className="bx bx-expand"></i>
                            </button>
                        </div>

                        <button
                            onClick={onClose}
                            className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center border border-transparent transition-all duration-200 hover:scale-105 active:scale-95 text-slate-500 hover:text-slate-800 group ml-2"
                            title={t('layouts.setup_close_preview')}
                        >
                            <i className="bx bx-x text-xl group-hover:rotate-90 transition-transform duration-300"></i>
                        </button>
                        </div>
                    </div>
                </div>

                {/* Main Content Area */}
                <div className="flex flex-1 overflow-hidden min-h-0 bg-gray-50/50 z-10">
                    {/* Left Side: Layout Canvas */}
                    <div ref={containerRef} className="flex-1 overflow-auto flex items-center justify-center bg-gray-100/50 relative p-4 custom-scrollbar">
                        {/* Dot Pattern Background similar to Editor */}
                        <div className="absolute inset-0 opacity-30 pointer-events-none" 
                            style={{ backgroundImage: 'radial-gradient(#cbd5e1 1.5px, transparent 1.5px)', backgroundSize: '20px 20px', backgroundAttachment: 'local' }}
                        ></div>

                        <div 
                             className="relative transition-all duration-200 ease-out z-10 bg-black rounded-2xl overflow-hidden shadow-[0_50px_100px_-20px_rgba(0,0,0,0.5),0_30px_60px_-30px_rgba(0,0,0,0.6)] ring-8 ring-slate-950"
                             style={{
                                width: `${scaledWidth}px`,
                                height: `${scaledHeight}px`,
                                flexShrink: 0 // Prevent shrinking
                             }}
                        >
                             <div 
                                className="absolute top-0 left-0 bg-black"
                                style={{
                                    width: `${displayDimensions.width}px`,
                                    height: `${displayDimensions.height}px`,
                                    transform: `scale(${finalScale})`,
                                    transformOrigin: 'top left',
                                }}
                            >
                                {areas.map((area, index) => (
                                    <PreviewAreaCanvasItem 
                                        key={area.id} 
                                        area={area} 
                                        index={index} 
                                        areasCount={areas.length} 
                                        forcedItem={areaContents[area.id]}
                                        onActiveItemChange={(item) => {
                                            // Only update if ID has changed to prevent render loops
                                            // And standard update (without _forceSelect) won't trigger jump in VisualAreaContent
                                            setAreaContents(prev => {
                                                if (prev[area.id]?._instanceId === item._instanceId) return prev;
                                                return { ...prev, [area.id]: item };
                                            });
                                            
                                            // Also update start time for progress bar sync
                                            setAreaStartTimes(prev => {
                                                // We can be smarter here, checking if item actually changed
                                                // but for now, syncing time on change is correct for progress bar
                                                return { ...prev, [area.id]: new Date() };
                                            });
                                        }}
                                        // Pass original dimensions if needed for px handling, though transform handles visual scaling
                                        // originalDimensions={displayDimensions} 
                                    />
                                ))}

                                {areas.length === 0 && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
                                        <div className="text-center">
                                            <i className="bx bx-grid-alt text-6xl text-gray-300 mb-4"></i>
                                            <p className="text-xl font-semibold text-gray-400">{t('layouts.setup_no_areas_defined')}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        
                         <div className="absolute bottom-4 left-4 flex items-center justify-between text-xs px-2 text-slate-500 z-50 pointer-events-none">
                            <div className="flex items-center gap-3 font-medium bg-white/80 backdrop-blur-sm px-2 py-1 rounded shadow-sm border border-gray-200">
                                <span>{aspectRatio.width}x{aspectRatio.height}</span>
                                <span>Scale: {Math.round(finalScale * 100)}%</span>
                            </div>
                        </div>
                    </div>

                    {/* Right Side: Sidebar */}
                    <div className="w-[350px] border-l border-gray-200 bg-white overflow-y-auto custom-scrollbar p-0 shadow-lg">
                         <div className="p-4 border-b border-gray-100 bg-gray-50/50">
                            <h4 className="text-sm font-bold text-slate-800">{t('layouts.setup_playlist_content')}</h4>
                            <p className="text-[10px] text-slate-500 mt-0.5 font-medium">{t('layouts.setup_content_by_area')}</p>
                        </div>
                        
                        <div className="p-2 space-y-3">
                            {areas.map((area, index) => (
                                <PreviewSidebarAreaItem 
                                    key={area.id}
                                    area={area}
                                    index={index}
                                    activeContent={areaContents[area.id]}
                                    areaStartTimes={areaStartTimes}
                                    onSelect={(areaId, item) => {
                                        // Force a new object reference to trigger useEffect in VisualAreaContent
                                        setAreaContents(prev => ({ ...prev, [areaId]: { ...item, _forceSelect: Date.now() } }));
                                        setAreaStartTimes(prev => ({ ...prev, [areaId]: new Date() }));
                                    }}
                                />
                            ))}
                            
                            {areas.length === 0 && (
                                <div className="p-8 text-center text-slate-400 bg-gray-50 mx-4 rounded-xl border border-dashed border-gray-200">
                                    <p className="text-xs font-medium">{t('layouts.setup_no_areas_found')}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
}

export default PreviewLayoutModal;
